import React, { useEffect, useState } from "react";
import AudioLayout from "../AudioLayout";
import { audioApi, formatErr } from "../api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import { ShieldCheck, BookOpen, Users, BarChart3, Trash2, Plus, Edit3 } from "lucide-react";

const TABS = [
  ["analytics", "Analytics", BarChart3],
  ["books", "Books & Vachanas", BookOpen],
  ["users", "Users", Users],
  ["subs", "Subscriptions", ShieldCheck],
];

export default function AudioAdmin() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("analytics");
  const [analytics, setAnalytics] = useState(null);
  const [books, setBooks] = useState([]);
  const [users, setUsers] = useState([]);
  const [subs, setSubs] = useState([]);
  const [openBookId, setOpenBookId] = useState(null);
  const [vachanas, setVachanas] = useState([]);

  const isAdmin = user?.role === "admin";

  const refresh = async () => {
    if (!isAdmin) return;
    try {
      const [a, b, u, s] = await Promise.all([
        audioApi.get("/admin/analytics").then(r => r.data).catch(() => null),
        audioApi.get("/admin/books").then(r => r.data).catch(() => []),
        audioApi.get("/admin/users").then(r => r.data).catch(() => []),
        audioApi.get("/admin/subscriptions").then(r => r.data).catch(() => []),
      ]);
      setAnalytics(a); setBooks(b); setUsers(u); setSubs(s);
    } catch (e) { toast.error(formatErr(e)); }
  };

  useEffect(() => { if (isAdmin) refresh(); }, [isAdmin]);
  useEffect(() => {
    if (openBookId) audioApi.get(`/admin/books/${openBookId}/vachanas`).then(r => setVachanas(r.data)).catch(() => {});
    else setVachanas([]);
  }, [openBookId]);

  if (loading) return <AudioLayout><div className="text-center py-20 text-white/60">Loading…</div></AudioLayout>;
  if (!user) {
    return (
      <AudioLayout>
        <div className="max-w-md mx-auto text-center py-16" data-testid="ac-admin-needs-login">
          <ShieldCheck className="mx-auto mb-4 text-amber-300" size={28}/>
          <h1 className="text-2xl font-bold">Admin sign in required</h1>
          <p className="text-white/65 mt-2 text-sm">This panel uses the donor-platform admin account.</p>
          <a href="/admin-login?next=/audio-comics/admin" className="mt-6 inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white text-black font-semibold">Open admin login</a>
        </div>
      </AudioLayout>
    );
  }
  if (!isAdmin) {
    return (
      <AudioLayout>
        <div className="max-w-md mx-auto text-center py-16" data-testid="ac-admin-forbidden">
          <h1 className="text-xl font-semibold">Forbidden</h1>
          <p className="text-white/60 text-sm mt-2">You are signed in as <b>{user.email}</b>, not an administrator.</p>
        </div>
      </AudioLayout>
    );
  }

  const addBook = async () => {
    const title = prompt("Book title?"); if (!title) return;
    const desc = prompt("Description?", "") || "";
    const thumb = prompt("Thumbnail URL?", "") || "";
    try { await audioApi.post("/admin/books", { title, description: desc, thumbnail_url: thumb, sort_order: books.length }); toast.success("Book added"); refresh(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const editBook = async (b) => {
    const title = prompt("Title", b.title); if (!title) return;
    const description = prompt("Description", b.description || "") || "";
    const thumbnail_url = prompt("Thumbnail URL", b.thumbnail_url || "") || "";
    try { await audioApi.put(`/admin/books/${b.book_id}`, { title, description, thumbnail_url }); toast.success("Updated"); refresh(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const delBook = async (b) => {
    if (!confirm(`Delete "${b.title}" and its vachanas?`)) return;
    try { await audioApi.delete(`/admin/books/${b.book_id}`); toast.success("Deleted"); refresh(); setOpenBookId(null); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const addVachana = async () => {
    const title = prompt("Vachana title?"); if (!title) return;
    const youtube_url = prompt("YouTube URL?"); if (!youtube_url) return;
    const description = prompt("Description?", "") || "";
    try {
      await audioApi.post(`/admin/books/${openBookId}/vachanas`, { title, description, youtube_url, sort_order: vachanas.length });
      toast.success("Vachana added");
      audioApi.get(`/admin/books/${openBookId}/vachanas`).then(r => setVachanas(r.data));
    } catch (e) { toast.error(formatErr(e)); }
  };
  const editVachana = async (v) => {
    const title = prompt("Title", v.title); if (!title) return;
    const youtube_url = prompt("YouTube URL", v.youtube_url || "") || v.youtube_url;
    const description = prompt("Description", v.description || "") || "";
    try {
      await audioApi.put(`/admin/vachanas/${v.vachana_id}`, { title, youtube_url, description });
      toast.success("Updated");
      audioApi.get(`/admin/books/${openBookId}/vachanas`).then(r => setVachanas(r.data));
    } catch (e) { toast.error(formatErr(e)); }
  };
  const delVachana = async (v) => {
    if (!confirm(`Delete vachana "${v.title}"?`)) return;
    try {
      await audioApi.delete(`/admin/vachanas/${v.vachana_id}`);
      toast.success("Deleted");
      audioApi.get(`/admin/books/${openBookId}/vachanas`).then(r => setVachanas(r.data));
    } catch (e) { toast.error(formatErr(e)); }
  };
  const toggleUser = async (u) => {
    try { await audioApi.put(`/admin/users/${u.user_id}`, { is_active: u.is_active === false }); refresh(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const delUser = async (u) => {
    if (!confirm(`Delete ${u.email}? This removes their sessions, subscription, and events.`)) return;
    try { await audioApi.delete(`/admin/users/${u.user_id}`); toast.success("Deleted"); refresh(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <AudioLayout>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-amber-300 mb-2">Audio Comics · Admin</div>
          <h1 className="text-3xl font-bold">Control room</h1>
          <p className="text-white/55 text-sm mt-1">Access mode: <b className="text-white/80">{analytics?.current_mode || "—"}</b></p>
        </div>
      </div>

      <div className="flex gap-1 border-b border-white/10 overflow-x-auto mb-6">
        {TABS.map(([k, l, Icon]) => (
          <button key={k} onClick={() => setTab(k)} className={`px-4 py-2.5 text-sm whitespace-nowrap inline-flex items-center gap-2 ${tab === k ? "border-b-2 border-amber-400 text-amber-300" : "text-white/60 hover:text-white"}`} data-testid={`ac-admin-tab-${k}`}>
            <Icon size={14}/> {l}
          </button>
        ))}
      </div>

      {tab === "analytics" && analytics && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Total users" value={analytics.total_users}/>
            <Stat label="Active subscribers" value={analytics.active_subscribers}/>
            <Stat label="Conversion %" value={`${analytics.conversion_rate_pct}%`}/>
            <Stat label="Avg session (s)" value={analytics.avg_session_seconds}/>
          </div>

          {analytics.mode_stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="ac-admin-mode-stats">
              <Stat label="Users during FREE" value={analytics.mode_stats.free_users}/>
              <Stat label="Plays during FREE" value={analytics.mode_stats.free_plays}/>
              <Stat label="Plays during PAID" value={analytics.mode_stats.paid_plays}/>
              <Stat label="Revenue (est.)"
                    value={`${analytics.mode_stats.currency === "INR" ? "₹" : analytics.mode_stats.currency}${analytics.mode_stats.revenue_estimate}`}/>
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-5">
            <Panel title="Most listened books">
              {analytics.top_books.length === 0 ? <Empty/> : (
                <ol className="space-y-2 text-sm">
                  {analytics.top_books.map((b, i) => (
                    <li key={b.book_id} className="flex items-center justify-between">
                      <span><span className="text-white/40 mr-2">{i + 1}.</span>{b.title}</span>
                      <span className="text-amber-300 font-semibold">{b.opens}</span>
                    </li>
                  ))}
                </ol>
              )}
            </Panel>
            <Panel title="Most listened vachanas">
              {analytics.top_vachanas.length === 0 ? <Empty/> : (
                <ol className="space-y-2 text-sm">
                  {analytics.top_vachanas.map((v, i) => (
                    <li key={v.vachana_id} className="flex items-center justify-between">
                      <span><span className="text-white/40 mr-2">{i + 1}.</span>{v.title}</span>
                      <span className="text-amber-300 font-semibold">{v.plays}</span>
                    </li>
                  ))}
                </ol>
              )}
            </Panel>
          </div>
          <Panel title="Per-user activity (top 20)">
            {analytics.per_user_activity.length === 0 ? <Empty/> : (
              <table className="w-full text-sm">
                <thead className="text-white/40 text-xs uppercase tracking-widest">
                  <tr><th className="text-left py-2">User</th><th className="text-right py-2">Events</th><th className="text-right py-2">Plays</th></tr>
                </thead>
                <tbody>
                  {analytics.per_user_activity.map(u => (
                    <tr key={u.user_id} className="border-t border-white/5">
                      <td className="py-2"><div>{u.name}</div><div className="text-[11px] text-white/40">{u.email}</div></td>
                      <td className="py-2 text-right">{u.events}</td>
                      <td className="py-2 text-right text-amber-300">{u.plays}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Panel>
        </div>
      )}

      {tab === "books" && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="font-semibold">{books.length} books</h2>
            <button onClick={addBook} className="inline-flex items-center gap-1 text-sm px-3 py-2 rounded-full bg-amber-400 text-black font-semibold hover:bg-amber-300" data-testid="ac-admin-book-add">
              <Plus size={14}/> Add book
            </button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {books.map(b => (
              <div key={b.book_id} className="rounded-xl bg-white/5 border border-white/10 overflow-hidden" data-testid={`ac-admin-book-${b.book_id}`}>
                <div className="aspect-[3/4] bg-black/40 relative">
                  {b.thumbnail_url && <img src={b.thumbnail_url} alt="" className="w-full h-full object-cover"/>}
                </div>
                <div className="p-3">
                  <div className="text-[10px] uppercase tracking-widest text-amber-300/80">{b.vachana_count} vachanas</div>
                  <div className="text-sm font-medium line-clamp-2 leading-snug min-h-[2.5em]">{b.title}</div>
                  <div className="mt-2 flex items-center gap-2 text-[11px]">
                    <button onClick={() => setOpenBookId(b.book_id)} className="text-amber-300 hover:text-amber-200">Vachanas</button>
                    <button onClick={() => editBook(b)} className="text-white/60 hover:text-white inline-flex items-center gap-1"><Edit3 size={11}/></button>
                    <button onClick={() => delBook(b)} className="text-rose-400 hover:text-rose-300 ml-auto inline-flex items-center gap-1"><Trash2 size={11}/></button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {openBookId && (
            <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setOpenBookId(null)} data-testid="ac-admin-vachana-modal">
              <div className="bg-[#161024] border border-white/10 rounded-2xl w-full max-w-2xl p-6 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold">Vachanas</h3>
                  <button onClick={addVachana} className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-amber-400 text-black font-semibold" data-testid="ac-admin-vachana-add">
                    <Plus size={12}/> Add vachana
                  </button>
                </div>
                <ul className="space-y-2">
                  {vachanas.map(v => (
                    <li key={v.vachana_id} className="flex items-center gap-3 p-3 rounded-lg bg-white/[0.04] border border-white/10">
                      <img src={v.thumbnail_url} alt="" className="w-14 h-14 rounded object-cover bg-black/40 shrink-0"/>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{v.title}</div>
                        <div className="text-[11px] text-white/50 truncate">{v.youtube_url}</div>
                      </div>
                      <button onClick={() => editVachana(v)} className="text-white/60 hover:text-white"><Edit3 size={13}/></button>
                      <button onClick={() => delVachana(v)} className="text-rose-400 hover:text-rose-300"><Trash2 size={13}/></button>
                    </li>
                  ))}
                  {vachanas.length === 0 && <li className="text-sm text-white/50 text-center py-8">No vachanas yet.</li>}
                </ul>
                <button onClick={() => setOpenBookId(null)} className="mt-4 text-xs text-white/50 hover:text-white">Close</button>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === "users" && (
        <Panel title={`${users.length} users`}>
          <table className="w-full text-sm">
            <thead className="text-white/40 text-xs uppercase tracking-widest">
              <tr><th className="text-left py-2">Name</th><th className="text-left py-2">Email</th><th className="text-left py-2">Status</th><th className="text-left py-2">Subscription</th><th></th></tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.user_id} className="border-t border-white/5" data-testid={`ac-admin-user-${u.user_id}`}>
                  <td className="py-2">{u.name}</td>
                  <td className="py-2 text-white/60">{u.email}</td>
                  <td className="py-2">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${u.is_active === false ? "bg-rose-500/15 text-rose-300" : "bg-emerald-500/15 text-emerald-300"}`}>{u.is_active === false ? "deactivated" : "active"}</span>
                  </td>
                  <td className="py-2">{u.subscribed ? <span className="text-amber-300">Pro · expires {u.subscription?.expiry_date?.slice(0, 10)}</span> : <span className="text-white/40">—</span>}</td>
                  <td className="py-2 text-right">
                    <button onClick={() => toggleUser(u)} className="text-amber-300 hover:text-amber-200 text-xs mr-3">{u.is_active === false ? "Activate" : "Deactivate"}</button>
                    <button onClick={() => delUser(u)} className="text-rose-400 hover:text-rose-300 text-xs"><Trash2 size={11} className="inline"/></button>
                  </td>
                </tr>
              ))}
              {users.length === 0 && <tr><td colSpan="5" className="py-8 text-center text-white/50">No users yet — sign in via Google to create one.</td></tr>}
            </tbody>
          </table>
        </Panel>
      )}

      {tab === "subs" && (
        <Panel title={`${subs.length} subscription records`}>
          <table className="w-full text-sm">
            <thead className="text-white/40 text-xs uppercase tracking-widest">
              <tr><th className="text-left py-2">User</th><th className="text-left py-2">Status</th><th className="text-left py-2">Started</th><th className="text-left py-2">Expires</th><th className="text-right py-2">Renewals</th></tr>
            </thead>
            <tbody>
              {subs.map(s => (
                <tr key={s.user_id} className="border-t border-white/5" data-testid={`ac-admin-sub-${s.user_id}`}>
                  <td className="py-2"><div>{s.user?.name}</div><div className="text-[11px] text-white/40">{s.user?.email}</div></td>
                  <td className="py-2">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${s.active_now ? "bg-emerald-500/15 text-emerald-300" : "bg-white/10 text-white/60"}`}>{s.active_now ? "active" : (s.status || "—")}</span>
                  </td>
                  <td className="py-2 text-white/60">{s.start_date?.slice(0, 10)}</td>
                  <td className="py-2 text-white/60">{s.expiry_date?.slice(0, 10)}</td>
                  <td className="py-2 text-right">{s.renewal_count || 0}</td>
                </tr>
              ))}
              {subs.length === 0 && <tr><td colSpan="5" className="py-8 text-center text-white/50">No subscriptions yet.</td></tr>}
            </tbody>
          </table>
        </Panel>
      )}
    </AudioLayout>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
      <div className="text-[10px] uppercase tracking-widest text-white/40">{label}</div>
      <div className="text-2xl font-bold mt-2">{value}</div>
    </div>
  );
}
function Panel({ title, children }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5">
      <div className="text-[10px] uppercase tracking-widest text-white/40 mb-4">{title}</div>
      {children}
    </div>
  );
}
function Empty() { return <div className="text-sm text-white/50 py-4 text-center">No data yet.</div>; }
